/*
 * CDR energy bills → the vault store's raw bill shape (the one
 * GET /vaults/{vault}/bills returns), so Bills History and bill details render
 * CDR invoices unchanged. CDR invoices follow the Consumer Data Standards
 * (invoiceNumber, issueDate, dueDate, invoiceAmount, period.startDate/endDate);
 * snake_case and already-mapped keys are accepted too.
 */

const firstDefined = (...values) => values.find(value => value !== undefined && value !== null && value !== '')

/*
 * The invoice list out of a cdr-bills response body, whichever envelope it uses
 * @param {*} data - response `data`
 * @returns {Array}
 */
export function cdrInvoiceList (data) {
  if (Array.isArray(data)) return data
  const list = firstDefined(data?.invoices, data?.bills, data?.data?.invoices, data?.data)
  return Array.isArray(list) ? list : []
}

/*
 * One CDR invoice (list item or detail) as a raw vault bill
 * @param {Object} invoice
 * @returns {Object|null} null when it has no invoice number to key it by
 */
export function cdrInvoiceToBill (invoice) {
  const source = invoice?.invoice || invoice
  const id = firstDefined(source?.invoiceNumber, source?.invoice_number, source?.id)
  if (id === undefined) return null

  const period = source?.period || source?.billing_period || {}
  const start = firstDefined(period.startDate, period.start_date, period.start)
  const end = firstDefined(period.endDate, period.end_date, period.end)

  const usage = source?.usage
  const usageValue = typeof usage === 'object' ? firstDefined(usage?.value, usage?.usage) : usage

  return {
    id: String(id),
    billed_date: firstDefined(source?.issueDate, source?.issue_date, source?.billed_date),
    amount: firstDefined(source?.invoiceAmount, source?.invoice_amount, source?.amount),
    amount_period: firstDefined(source?.amount_period, source?.amountPeriod) ?? null,
    gst_amount: firstDefined(source?.gst_amount, source?.gstAmount) ?? null,
    billing_period: start && end ? { start, end, label: period.label ?? null } : null,
    next_payment_date: firstDefined(source?.dueDate, source?.due_date, source?.next_payment_date) ?? null,
    usage: usageValue !== undefined
      ? { value: usageValue, unit: firstDefined(usage?.unit, usage?.measureUnit, source?.usage_unit) ?? 'kWh' }
      : null,
    // The bill's PDF, when the API sends one ({file_name, file_url, size_bytes,
    // caption}, as email bills do); cdr-bills sends none yet.
    source_document: firstDefined(source?.source_document, source?.sourceDocument) ?? null,
    payment_status: firstDefined(source?.payment_status, source?.paymentStatus) ?? null,
    usage_rate: firstDefined(source?.usage_rate, source?.usageRate) ?? null,
    discount_amount: firstDefined(source?.discount_amount, source?.discountAmount) ?? null,
    // CDR bills carry every field, so bill details shows them all, latest included.
    is_cdr: true
  }
}

/*
 * The Bills History summary figures a cdr-bills response carries (count,
 * period_months), or null when it sends none
 * @param {*} data - response `data`
 * @returns {{count: number|null, periodMonths: number|null}|null}
 */
export function cdrBillsSummary (data) {
  const toCount = value => (Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value) : null)
  const count = toCount(data?.count)
  const periodMonths = toCount(firstDefined(data?.period_months, data?.periodMonths))
  return count === null && periodMonths === null ? null : { count, periodMonths }
}

/*
 * Whether a cdr-consent response body holds a consent
 * @param {*} data - response `data`
 * @returns {boolean}
 */
export function hasCdrConsent (data) {
  if (Array.isArray(data)) return data.length > 0
  return !!data && typeof data === 'object' && Object.keys(data).length > 0
}
